import { Component, HostListener, Input, OnInit, Optional, Self, Inject, LOCALE_ID } from '@angular/core';
import { NgControl, NG_VALIDATORS } from '@angular/forms';
import { FormControlBase } from '../../common/form-control-base/form-control-base';
import { MathHelper } from '../../helpers/math-helper';
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { NumberValidator } from '../../validators/number-validator';
import { MaskitoOptions } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';

export let numberValidator = new NumberValidator();

@Component({
  selector: 'waf-number-input',
  templateUrl: './number-input.component.html',
  inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
  providers: [
    { provide: NG_VALIDATORS, useValue: numberValidator, multi: true }
  ],
  standalone: false
})
export class NumberInputComponent extends FormControlBase<any> implements OnInit {

  @Input() maxlength = 9;

  @Input() allowDecimal = true;

  @Input() allowNegative = true;

  @Input() decimalLimit = 2;

  @Input() novalidate = false;

  @Input() useThousandsSeparator = false;

  internalValue: any;

  maskitoOptions: MaskitoOptions;

  private decimalSymbol: string;
  private thousandSeparator: string;

  constructor(@Self() @Optional() protected override ngModel: NgControl, @Inject(LOCALE_ID) private locale: string) {
    super('waf-number-input', ngModel);
  }


  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    if (e.key == '_') {
      e.preventDefault();
      return;
    }
  }

  override onInit(): void {
    this.setupMask();
  }

  private setupMask(): void {
    const groupSymbol = getLocaleNumberSymbol(this.locale, NumberSymbol.Group);
    this.decimalSymbol = getLocaleNumberSymbol(this.locale, NumberSymbol.Decimal);
    this.thousandSeparator = this.useThousandsSeparator ? (groupSymbol || ',') : '';

    // Create base options without min/max to prevent automatic value clamping
    const baseOptions = maskitoNumberOptionsGenerator({
      maximumFractionDigits: this.allowDecimal ? this.decimalLimit : 0,
      minimumFractionDigits: 0,
      thousandSeparator: this.thousandSeparator,
      decimalSeparator: this.decimalSymbol
    });

    // Add custom preprocessor to limit character length without changing values
    this.maskitoOptions = {
      ...baseOptions,
      preprocessors: [
        ...(baseOptions.preprocessors || []),
        ({ elementState, data }, actionType) => {
          if (actionType === 'insert' && data) {
            const dataString = typeof data === 'string' ? data : String(data);
            if (!this.allowNegative && (dataString.includes('-') || dataString.includes('−'))) {
              return { elementState, data: '' };
            }

            const currentValue = elementState.value;
            const selection = elementState.selection;
            const newValue = currentValue.slice(0, selection[0]) + data + currentValue.slice(selection[1]);

            const numericValue = newValue.replace(new RegExp(`[^\\d${this.decimalSymbol}${this.allowNegative ? '\\-' : ''}]`, 'g'), '');
            const digitCount = numericValue.replace(new RegExp(`[${this.decimalSymbol}\\-]`, 'g'), '').length;

            if (digitCount > this.maxlength) {
              return { elementState, data: '' };
            }
          }
          return { elementState, data };
        }
      ]
    };
  }



  override onValueWritten() {
    if (this.value != null) {
      this.internalValue = this.allowDecimal === true ?
        MathHelper.roundToDecimalPlaces(this.value, this.decimalLimit).toString().replace('.', this.decimalSymbol) :
        this.value.toFixed(0);
    }
    else {
      this.internalValue = '';
    }
  }

  onInternalValueChanged(value: any) {
    if (typeof value === 'number') {
      value = value.toString();
    }
    this.internalValue = value;

    if (value) {
      const regex = new RegExp(`[^-0-9${this.decimalSymbol}]`, 'g');
      let typedValue = value.replace(regex, "").replace(this.decimalSymbol, '.');

      const isNegative = String(value).trim().startsWith('-') || String(value).trim().includes('−') || parseFloat(String(value)) < 0;
      if (isNegative) {
        typedValue = typedValue.replace('−', '-');

        if (!typedValue.startsWith('-')) {
          typedValue = '-' + typedValue.replace(/^-+/, '');
        }
        if (!/^-?\d*\.?\d*$/.test(typedValue))
        {
          this.value = null; return;
        }
      }
      this.value = this.allowDecimal === true ? parseFloat(typedValue) : parseInt(typedValue);
    } else {
      this.value = null;
    }
  }
}
